import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, StatusBadge } from "@/components/DataTable";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Wallet } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function FeesPage() {
  const [fees, setFees] = useState([]);
  const [payOpen, setPayOpen] = useState(null); // holds fee obj
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("UPI");
  const [rzpConfig, setRzpConfig] = useState({ enabled: false, mock_mode: true });

  const load = async () => setFees((await api.get("/fees")).data);
  useEffect(()=>{
    load();
    api.get("/razorpay/config").then(({data}) => setRzpConfig(data)).catch(()=>{});
  },[]);

  const totals = fees.reduce((acc, f) => {
    acc.total += Number(f.amount||0);
    acc.paid += Number(f.paid||0);
    acc.due += Math.max(0, Number(f.amount||0) - Number(f.paid||0));
    return acc;
  }, {total:0, paid:0, due:0});

  const doPay = async (e) => {
    e.preventDefault();
    const newPaid = Math.min((payOpen.paid||0) + Number(amount), payOpen.amount);
    const status = newPaid >= payOpen.amount ? "paid" : (newPaid > 0 ? "partial" : "due");
    await api.put(`/fees/${payOpen.id}`, { ...payOpen, paid:newPaid, status });
    await api.post("/payments", {
      amount: Number(amount), method, student_name: payOpen.student_name,
      receipt_no: `RCP-${Date.now().toString().slice(-6)}`, fee_id: payOpen.id,
    });
    toast.success("Payment recorded");
    setPayOpen(null); setAmount(0); load();
  };

  const payOnline = async (fee) => {
    try {
      const { data: order } = await api.post("/razorpay/orders", { fee_id: fee.id });

      if (order.mock_mode) {
        // Skip Razorpay checkout in mock mode — call verify directly
        const mockPaymentId = "pay_MOCK" + Date.now();
        await api.post("/razorpay/verify", {
          fee_id: fee.id,
          razorpay_order_id: order.order_id,
          razorpay_payment_id: mockPaymentId,
          razorpay_signature: "mock_sig",
          mock: true,
        });
        toast.success("Payment successful (MOCK) — Razorpay keys not configured");
        load();
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) return toast.error("Razorpay SDK failed to load");
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "CoachERP",
        description: `Fee payment for ${fee.student_name}`,
        order_id: order.order_id,
        prefill: { name: fee.student_name },
        theme: { color: "#0A66C2" },
        handler: async (response) => {
          try {
            await api.post("/razorpay/verify", {
              fee_id: fee.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful");
            load();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast("Payment cancelled") },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => toast.error("Payment failed: " + (r.error?.description || "unknown")));
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment");
    }
  };

  return (
    <div>
      <PageHeader title="Fees" subtitle="Collect, track and reconcile" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="linkedin-card p-4" data-testid="fees-total-card">
          <div className="kpi-label">Total Billed</div>
          <div className="text-2xl font-semibold text-[#000000E6] mt-1">₹{totals.total.toLocaleString('en-IN')}</div>
        </div>
        <div className="linkedin-card p-4" data-testid="fees-collected-card">
          <div className="kpi-label">Collected</div>
          <div className="text-2xl font-semibold text-[#057642] mt-1">₹{totals.paid.toLocaleString('en-IN')}</div>
        </div>
        <div className="linkedin-card p-4" data-testid="fees-due-card">
          <div className="kpi-label">Outstanding</div>
          <div className="text-2xl font-semibold text-[#CC1016] mt-1">₹{totals.due.toLocaleString('en-IN')}</div>
        </div>
      </div>

      <DataTable
        testid="fees-table"
        columns={[
          { key:"student_name", label:"Student" },
          { key:"course_name", label:"Course" },
          { key:"amount", label:"Amount", render: r => `₹${(r.amount||0).toLocaleString('en-IN')}` },
          { key:"paid", label:"Paid", render: r => `₹${(r.paid||0).toLocaleString('en-IN')}` },
          { key:"due_date", label:"Due Date" },
          { key:"status", label:"Status", render: r => <StatusBadge status={r.status}/> },
          { key:"actions", label:"", render: r => (
            r.status !== "paid" ? (
              <div className="flex items-center gap-2">
                <button className="text-xs px-3 py-1 rounded-full bg-[#0A66C2] text-white hover:bg-[#004182] font-semibold inline-flex items-center gap-1"
                  data-testid={`payonline-btn-${r.id}`}
                  onClick={()=>payOnline(r)}>
                  <CreditCard size={12}/> Pay online{rzpConfig.mock_mode ? " (mock)" : ""}
                </button>
                <button className="text-xs px-3 py-1 rounded-full bg-[#EDEBE7] text-[#000000E6] hover:bg-[#E0DFDC] font-semibold inline-flex items-center gap-1"
                  data-testid={`pay-btn-${r.id}`}
                  onClick={()=>{setPayOpen(r); setAmount((r.amount||0)-(r.paid||0));}}>
                  <Wallet size={12}/> Collect
                </button>
              </div>
            ) : <span className="text-xs text-[#057642] font-semibold">Cleared</span>
          )},
        ]}
        rows={fees}
      />

      <Dialog open={!!payOpen} onOpenChange={(v)=>!v && setPayOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Collect payment</DialogTitle></DialogHeader>
          {payOpen && (
            <form onSubmit={doPay} className="space-y-3">
              <div className="text-sm text-[#00000099]">Student: <span className="text-[#000000E6] font-semibold">{payOpen.student_name}</span></div>
              <input type="number" required placeholder="Amount" data-testid="payment-amount-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={amount} onChange={e=>setAmount(e.target.value)}/>
              <select data-testid="payment-method-select" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={method} onChange={e=>setMethod(e.target.value)}>
                {["UPI","Card","Cash","NetBanking"].map(x=><option key={x}>{x}</option>)}
              </select>
              <DialogFooter><button type="submit" className="btn-primary" data-testid="payment-confirm-btn">Record payment</button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
